// src/tools/ToolsLayout.tsx
import React, { Suspense, useState } from 'react';
import { NavLink, Link, Outlet, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowLeft, FaBars, FaTimes, FaTools } from 'react-icons/fa';
import { orderedCategories, toolsInCategory, CATEGORY_LABELS } from './registry';

const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  background: ${(p) => p.theme.gradients.background};
`;

const Sidebar = styled.aside<{ $open: boolean }>`
  width: 260px;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  align-self: flex-start;
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 1.4rem 1rem;
  gap: 0.3rem;
  background: rgba(14, 14, 18, 0.6);
  border-right: 1px solid ${(p) => p.theme.colors.glassBorder};
  backdrop-filter: blur(12px);
  overflow-y: auto;

  @media (max-width: 860px) {
    position: fixed;
    z-index: 1000;
    transform: translateX(${(p) => (p.$open ? '0' : '-105%')});
    transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
  }
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  padding: 0.3rem 0.6rem 1rem;
  background: linear-gradient(135deg, #7c3aed, #a78bfa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  svg { color: #a78bfa; -webkit-text-fill-color: initial; }
`;

const BackLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.82rem;
  color: ${(p) => p.theme.colors.muted};
  padding: 0.55rem 0.7rem;
  border-radius: 9px;
  margin-bottom: 0.4rem;
  transition: all 0.2s ease;

  &:hover { color: ${(p) => p.theme.colors.white}; background: rgba(255, 255, 255, 0.04); }
`;

const SectionLabel = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.muted};
  padding: 0.4rem 0.7rem;
`;

const ToolLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.6rem 0.7rem;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 500;
  color: ${(p) => p.theme.colors.light};
  border: 1px solid transparent;
  transition: all 0.18s ease;

  svg { font-size: 0.9rem; color: ${(p) => p.theme.colors.muted}; flex-shrink: 0; }

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    color: ${(p) => p.theme.colors.white};
  }

  &.active {
    background: rgba(139, 92, 246, 0.12);
    border-color: rgba(139, 92, 246, 0.3);
    color: ${(p) => p.theme.colors.white};
    svg { color: ${(p) => p.theme.colors.accentLight}; }
  }
`;

const Content = styled.main`
  flex: 1;
  min-width: 0;
  padding: 2.4rem clamp(1.2rem, 4vw, 3rem);
`;

const MobileBar = styled.div`
  display: none;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 1.4rem;

  @media (max-width: 860px) { display: flex; }
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid ${(p) => p.theme.colors.glassBorder};
  color: ${(p) => p.theme.colors.light};
  cursor: pointer;
  font-size: 1.05rem;
`;

const Backdrop = styled.div<{ $open: boolean }>`
  display: none;
  @media (max-width: 860px) {
    display: ${(p) => (p.$open ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    z-index: 999;
  }
`;

const Loading = styled.div`
  color: ${(p) => p.theme.colors.muted};
  font-family: ${(p) => p.theme.fonts.code};
  padding: 2rem 0;
`;

const ToolsLayout: React.FC = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const close = () => setOpen(false);

  // Close the mobile drawer whenever the route changes.
  React.useEffect(() => setOpen(false), [location.pathname]);

  return (
    <Shell>
      <Backdrop $open={open} onClick={close} />
      <Sidebar $open={open}>
        <Brand to="/tools" onClick={close}>
          <FaTools /> DevTools
        </Brand>
        <BackLink to="/">
          <FaArrowLeft /> Back to portfolio
        </BackLink>
        {orderedCategories.map((cat) => (
          <React.Fragment key={cat}>
            <SectionLabel>{CATEGORY_LABELS[cat]}</SectionLabel>
            {toolsInCategory(cat).map((tool) => (
              <ToolLink key={tool.slug} to={`/tools/${tool.slug}`} onClick={close}>
                <tool.icon />
                {tool.name}
              </ToolLink>
            ))}
          </React.Fragment>
        ))}
      </Sidebar>

      <Content>
        <MobileBar>
          <IconButton onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
            {open ? <FaTimes /> : <FaBars />}
          </IconButton>
          <Brand to="/tools" style={{ padding: 0 }}>
            <FaTools /> DevTools
          </Brand>
        </MobileBar>
        <Suspense fallback={<Loading>Loading tool…</Loading>}>
          <Outlet />
        </Suspense>
      </Content>
    </Shell>
  );
};

export default ToolsLayout;
